import { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, Check } from 'lucide-react';

// Preset ranges — all return { from, to } as ISO YYYY-MM-DD or nulls.
function isoDate(d) { return d.toISOString().slice(0, 10); }

export const PRESETS = [
  { id: 'last_7',   label: 'Last 7 days',   compute: () => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - 7);   return { from: isoDate(f), to: isoDate(t) }; } },
  { id: 'last_30',  label: 'Last 30 days',  compute: () => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - 30);  return { from: isoDate(f), to: isoDate(t) }; } },
  { id: 'last_90',  label: 'Last 90 days',  compute: () => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - 90);  return { from: isoDate(f), to: isoDate(t) }; } },
  { id: 'ytd',      label: 'Year to date',  compute: () => { const t = new Date(); const f = new Date(t.getFullYear(), 0, 1);          return { from: isoDate(f), to: isoDate(t) }; } },
  { id: 'last_12',  label: 'Last 12 months',compute: () => { const t = new Date(); const f = new Date(); f.setMonth(f.getMonth() - 12); return { from: isoDate(f), to: isoDate(t) }; } },
  { id: 'all',      label: 'All time',      compute: () => ({ from: null, to: null }) },
];

export default function DateRangePicker({ value, onChange, defaultPreset = 'all', testid = 'date-range' }) {
  const [open, setOpen] = useState(false);
  const [presetId, setPresetId] = useState(defaultPreset);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    if (!value) return;
    setCustomFrom(value.from || '');
    setCustomTo(value.to || '');
  }, [value]);

  const currentLabel = (() => {
    if (presetId === 'custom') {
      if (customFrom || customTo) return `${customFrom || '…'} → ${customTo || '…'}`;
      return 'Custom';
    }
    return PRESETS.find(p => p.id === presetId)?.label || 'All time';
  })();

  const pick = (p) => {
    setPresetId(p.id);
    const r = p.compute();
    setCustomFrom(r.from || ''); setCustomTo(r.to || '');
    onChange?.(r);
    setOpen(false);
  };

  const applyCustom = () => {
    setPresetId('custom');
    onChange?.({ from: customFrom || null, to: customTo || null });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="rounded-full h-10 px-4 gap-2 text-sm bg-white" data-testid={`${testid}-trigger`}>
          <CalendarIcon size={14} />
          <span>{currentLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" data-testid={`${testid}-panel`}>
        <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold mb-2">Presets</div>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          {PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => pick(p)}
              data-testid={`${testid}-preset-${p.id}`}
              className={`text-left text-[13px] px-3 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                presetId === p.id
                  ? 'bg-[#F1EDF5] border-[#BAA9CA] text-[#2D2833] font-semibold'
                  : 'border-[#E8E5EC] text-[#685B72] hover:bg-[#FAF8FB]'
              }`}
            >
              {presetId === p.id && <Check size={12} className="text-[#685B72]" />}
              {p.label}
            </button>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold mb-2">Custom range</div>
        <div className="grid grid-cols-2 gap-2 items-end">
          <label className="text-[11px] text-[#685B72]">
            From
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                   className="w-full mt-1 h-9 px-2 rounded-md border border-[#E8E5EC] text-[13px]" data-testid={`${testid}-from`} />
          </label>
          <label className="text-[11px] text-[#685B72]">
            To
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                   className="w-full mt-1 h-9 px-2 rounded-md border border-[#E8E5EC] text-[13px]" data-testid={`${testid}-to`} />
          </label>
          <Button onClick={applyCustom} disabled={!customFrom && !customTo}
                  className="col-span-2 h-9 rounded-md bg-[#685B72] hover:bg-[#685B72] text-white text-[13px]"
                  data-testid={`${testid}-apply`}>
            Apply custom range
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
